import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import pb, { apiCall } from '../services/pocketbase';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiAlertCircle,
  FiArrowLeft,
  FiCheck,
  FiClipboard,
  FiCopy,
  FiExternalLink,
  FiLayers,
  FiList,
  FiLock,
  FiPlay,
  FiPlayCircle,
  FiPlus,
  FiSearch,
  FiShield,
  FiUser,
  FiUsers,
  FiVideo,
  FiX,
  FiZap
} = FiIcons;

const VALID_SCOPES = ['client_facing', 'bpas_internal_team'];

const escapeFilterValue = (value) => String(value || '')
  .replace(/\\/g, '\\\\')
  .replace(/"/g, '\\"');

const formatDuration = (seconds) => {
  const value = Number(seconds);
  if (!Number.isFinite(value) || value <= 0) return '';
  const minutes = Math.floor(value / 60);
  const remainder = Math.floor(value % 60);
  return `${minutes}:${String(remainder).padStart(2, '0')}`;
};

const workflowTone = (status) => {
  switch (status) {
    case 'published': return 'bg-green-500/10 text-green-700 border-green-500/20';
    case 'review': return 'bg-amber-500/10 text-amber-700 border-amber-500/20';
    case 'archived': return 'bg-red-500/10 text-red-700 border-red-500/20';
    default: return 'bg-slate-800 text-slate-500 border-slate-700';
  }
};

const AppSetup = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [app, setApp] = useState(null);
  const [videos, setVideos] = useState([]);
  const [topics, setTopics] = useState([]);
  const [videoCount, setVideoCount] = useState(0);
  const [topicCount, setTopicCount] = useState(0);
  const [latestVideo, setLatestVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');
  const [workspaceQuery, setWorkspaceQuery] = useState('');
  const [searchResults, setSearchResults] = useState({ videos: [], topics: [] });
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');

  const requestedScope = searchParams.get('scope');
  const setupScope = VALID_SCOPES.includes(requestedScope)
    ? requestedScope
    : VALID_SCOPES.includes(app?.default_visibility_scope)
      ? app.default_visibility_scope
      : latestVideo?.visibility_scope || '';

  useEffect(() => {
    loadWorkspace();
  }, [id]);

  useEffect(() => {
    const query = workspaceQuery.trim();
    if (query.length < 2) {
      setSearchResults({ videos: [], topics: [] });
      setSearchError('');
      setSearchLoading(false);
      return undefined;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      const safeId = String(id || '').replace(/[^A-Za-z0-9]/g, '');
      if (!safeId) return;

      try {
        setSearchLoading(true);
        setSearchError('');
        const term = escapeFilterValue(query);
        const appClause = `app_ids ?= "${safeId}"`;
        const [videoResult, topicResult] = await Promise.all([
          apiCall(() => pb.collection('videos').getList(1, 8, {
            filter: `${appClause} && workflow_status != "archived" && (title ~ "${term}" || description ~ "${term}" || keywords ~ "${term}" || tags ~ "${term}" || category ~ "${term}")`,
            sort: '-updated'
          })),
          apiCall(() => pb.collection('playlists').getList(1, 6, {
            filter: `${appClause} && workflow_status != "archived" && (name ~ "${term}" || description ~ "${term}" || tags ~ "${term}")`,
            sort: '-updated'
          }))
        ]);
        if (cancelled) return;
        setSearchResults({ videos: videoResult.items || [], topics: topicResult.items || [] });
      } catch (searchFailure) {
        if (cancelled) return;
        console.error('App workspace search failed:', searchFailure);
        setSearchResults({ videos: [], topics: [] });
        setSearchError('Could not search this App right now.');
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [workspaceQuery, id]);

  const loadWorkspace = async () => {
    try {
      setLoading(true);
      setError('');
      const safeId = String(id || '').replace(/[^A-Za-z0-9]/g, '');
      if (!safeId) throw new Error('Invalid app identifier');

      const videoFilter = `app_ids ?= "${safeId}" && workflow_status != "archived"`;
      const topicFilter = `app_ids ?= "${safeId}" && workflow_status != "archived"`;
      const [appRecord, videoResult, recentVideoResult, topicResult, recentTopicResult] = await Promise.all([
        apiCall(() => pb.collection('apps').getOne(safeId)),
        apiCall(() => pb.collection('videos').getList(1, 1, { filter: videoFilter })),
        apiCall(() => pb.collection('videos').getList(1, 6, { filter: videoFilter, sort: '-updated' })),
        apiCall(() => pb.collection('playlists').getList(1, 1, { filter: topicFilter })),
        apiCall(() => pb.collection('playlists').getList(1, 4, { filter: topicFilter, sort: 'sort_order,-updated' }))
      ]);

      setApp(appRecord);
      setVideoCount(videoResult.totalItems);
      setTopicCount(topicResult.totalItems);
      setVideos(recentVideoResult.items || []);
      setTopics(recentTopicResult.items || []);
      setLatestVideo(recentVideoResult.items?.[0] || null);
    } catch (loadError) {
      console.error('Failed to load App workspace:', loadError);
      setError('Could not load this App workspace. Confirm PocketBase access and try again.');
    } finally {
      setLoading(false);
    }
  };

  const smartDefaults = useMemo(() => {
    if (!latestVideo) {
      return {
        source: 'App default',
        scope: setupScope,
        audience: '',
        channels: setupScope === 'client_facing' ? ['in_app_training_page'] : setupScope === 'bpas_internal_team' ? ['internal_sop_library'] : [],
        category: 'onboarding'
      };
    }

    return {
      source: `Latest video: ${latestVideo.title || 'Untitled'}`,
      scope: latestVideo.visibility_scope || setupScope,
      audience: latestVideo.visibility_scope === 'client_facing'
        ? latestVideo.client_audience_role
        : latestVideo.internal_audience_role,
      channels: Array.isArray(latestVideo.distribution_channels) ? latestVideo.distribution_channels : [],
      category: latestVideo.category || 'onboarding'
    };
  }, [latestVideo, setupScope]);

  const setPrimaryScope = async (scope) => {
    if (!VALID_SCOPES.includes(scope)) return;

    const next = new URLSearchParams(searchParams);
    next.set('scope', scope);
    setSearchParams(next, { replace: true });
    setApp(current => current ? { ...current, default_visibility_scope: scope } : current);

    // Persist when the optional App-level field exists. This remains non-blocking
    // so the workspace is still usable before the small schema patch is imported.
    try {
      await pb.collection('apps').update(id, { default_visibility_scope: scope });
    } catch (scopeError) {
      console.warn('Could not persist App default scope yet; using current workspace selection.', scopeError?.status || '');
    }
  };

  const buildVideoUrl = () => {
    const params = new URLSearchParams({ appId: id, source: 'app' });
    if (setupScope) params.set('scope', setupScope);
    return `/videos/new?${params.toString()}`;
  };

  const buildTopicUrl = () => {
    const params = new URLSearchParams({ appId: id, source: 'app' });
    if (setupScope) params.set('scope', setupScope);
    return `/playlists/new?${params.toString()}`;
  };

  const trainingUrl = app?.training_library_key
    ? `${window.location.origin}/training/${encodeURIComponent(app.training_library_key)}`
    : '';

  const integrationPrompt = app ? `PROJECT TASK: Integrate BPA Video Hub Training Centre into the EXISTING client application\n\nIMPORTANT ASSUMPTION\nThe client application already exists and is working. DO NOT rebuild, redesign or replace the application. Make a surgical addition only: add a Help & Training / Training Centre section that opens this App's BPA Video Hub training experience for the already signed-in user.\n\nVIDEO HUB APP DETAILS\n- App Name: ${app.app_name}\n- App Slug: ${app.app_slug}\n- Video Hub App Record ID: ${app.id}\n- Video Hub Origin: ${window.location.origin}\n- Default Content Scope: ${setupScope || 'NOT_YET_SELECTED'}\n- Published/active training records currently visible to admin: ${videoCount} video(s)\n- Topic/Playlist records currently visible to admin: ${topicCount}\n- Secure Training Library Route: ${trainingUrl || 'BACKEND_SETUP_PENDING — do not invent a route or key'}\n\nDESIRED CLIENT-APP UX\n1. Add one navigation item called “Help & Training” or “Training Centre” using the EXISTING client app navigation style.\n2. Add one authenticated route/page inside the EXISTING app. Do not create another login.\n3. Prefer embedding the BPA Training Library in an iframe so the user stays inside the signed-in client app. Use a popup only if the existing architecture makes iframe embedding impossible.\n4. The BPA Training Library itself provides topic grouping, video thumbnails/descriptions and smart search. Do not duplicate those features in the host app; provide a clean responsive container around the library.\n5. Match the host app's existing spacing, navigation, typography and mobile behaviour. Do not redesign unrelated screens.\n\nSECURITY BOUNDARY — NON-NEGOTIABLE\n- The browser must NEVER contain PocketBase superuser/admin credentials, Video Hub integration secrets, privileged API tokens or server credentials.\n- Do not place privileged values in VITE_ variables, frontend source, localStorage, sessionStorage, query strings, URLs, copied prompts or public build output.\n- A Training Library URL/key is an identifier, not an authentication credential. A copied direct URL must reveal no training content by itself.\n- The current signed-in client user must be validated by the CLIENT APP'S TRUSTED BACKEND before any Video Hub library payload is supplied.\n- The trusted backend must return only content authorised for this exact App Record ID / App Slug and the current audience role.\n- Never fetch Video Hub admin collections with privileged credentials directly from browser code.\n\nTRAINING LIBRARY HOST HANDSHAKE\nUse the existing BPA Video Hub message contract rather than inventing another one:\n- Library -> host: BPA_VIDEO_HUB_READY\n- Host -> library after trusted-backend authorisation: BPA_VIDEO_HUB_LIBRARY\n- Library -> host after accepting the authorised payload: BPA_VIDEO_HUB_AUTHORIZED\n- Library -> host while active: BPA_VIDEO_HUB_HEARTBEAT\n- Host -> library heartbeat response: BPA_VIDEO_HUB_ALIVE\n- Host -> library on logout/session revocation: BPA_VIDEO_HUB_REVOKE\n\nFor every message:\n- verify event.source is the expected iframe/popup window;\n- verify event.origin exactly matches the Video Hub Origin above;\n- preserve and verify requestId and libraryKey;\n- never use postMessage('*') for authorised content or credentials.\n\nAUTHORISED LIBRARY PAYLOAD\nThe trusted backend should supply only sanitised fields required for display, for example:\n{\n  app: { app_name, app_slug },\n  audienceRole,\n  videos: [{ id, title, description, video_url, thumbnail_url, category, tags, keywords, duration_seconds, sort_order }],\n  playlists: [{ id, name, description, tags, items: [{ id, position, video }] }]\n}\n\nOnly include records that are authorised for this App/user. Published client-facing training should remain App-scoped and audience-scoped. Do not include internal-only, draft, review or archived content in a client library payload.\n\nIF THE SECURE TRAINING ROUTE ABOVE SAYS BACKEND_SETUP_PENDING\n- You may add the host app navigation/page/container structure now, but DO NOT create a fake library key, hardcode credentials or bypass authorisation.\n- Clearly report that live Video Hub content wiring must wait until the secure Video Hub backend configuration supplies the real Training Library route and trusted-backend retrieval path.\n\nIMPLEMENTATION GUARDRAILS\n- Pull/use the latest source of truth for the existing client app before editing.\n- Preserve all current routes, authentication, business logic and visual design.\n- Prefer existing components/services; no new dependency unless essential.\n- Make the minimum files necessary.\n- Keep mobile/responsive behaviour intact.\n- On client logout, immediately send BPA_VIDEO_HUB_REVOKE and remove/clear the training iframe or popup state.\n- If host-session heartbeat stops, the library must relock.\n- Do not expose secrets in console logs or error UI.\n\nACCEPTANCE CHECKS\n- Signed-in authorised user can open Help & Training from inside the client app.\n- Correct App's Topics and videos appear with search.\n- Wrong App/audience content never appears.\n- Directly copying/opening the Training Library URL outside the signed-in host app reveals no training content.\n- Logout/revocation removes access promptly.\n- Existing client app features and UI remain unchanged outside the new Training Centre integration.\n\nBefore coding, inspect the existing client app's auth and trusted-backend pattern and adapt this integration to that architecture. Do not invent secrets, backend fields or credentials.` : '';

  const copyText = async (key, value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      window.setTimeout(() => setCopied(''), 1800);
    } catch (copyError) {
      console.error('Could not copy App setup content:', copyError);
      setError('Could not copy to the clipboard. Please copy the value manually.');
    }
  };

  const scopeLabel = smartDefaults.scope === 'client_facing'
    ? 'Client Facing'
    : smartDefaults.scope === 'bpas_internal_team'
      ? 'Internal Team'
      : 'Not selected';

  const audienceLabel = smartDefaults.audience
    ? smartDefaults.audience.replaceAll('_', ' ')
    : 'Confirm on first video';

  const totalSearchResults = searchResults.videos.length + searchResults.topics.length;

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto py-12 text-center">
        <div className="auth-spinner !w-8 !h-8 !mb-3" />
        <p className="text-sm font-semibold text-white">Preparing App workspace…</p>
      </div>
    );
  }

  if (error && !app) {
    return (
      <div className="max-w-3xl mx-auto py-10">
        <div className="bg-red-500/10 border border-red-500/20 text-red-700 px-4 py-4 rounded-xl flex items-start gap-3">
          <SafeIcon icon={FiAlertCircle} className="w-5 h-5 mt-0.5" />
          <div>
            <p className="font-semibold">App workspace unavailable</p>
            <p className="text-sm mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto pb-14">
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-5 mb-7">
        <div className="flex items-start gap-4">
          <button type="button" onClick={() => navigate('/apps')} className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-blue-600">
            <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
          </button>
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-500/10 text-blue-700 border border-blue-500/20">
                <SafeIcon icon={FiZap} className="w-3.5 h-3.5" /> Smart App Setup
              </span>
              <span className={`status-badge ${app?.is_active ? 'status-badge-active' : 'status-badge-inactive'}`}>
                {app?.is_active ? 'Active' : 'Inactive'}
              </span>
              {setupScope === 'client_facing' && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-blue-500/10 text-blue-700 border border-blue-500/20">
                  <SafeIcon icon={FiUsers} className="w-3.5 h-3.5" /> Client Facing
                </span>
              )}
              {setupScope === 'bpas_internal_team' && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-purple-500/10 text-purple-700 border border-purple-500/20">
                  <SafeIcon icon={FiUser} className="w-3.5 h-3.5" /> Internal Team
                </span>
              )}
            </div>
            <h1 className="text-3xl font-bold text-white">{app?.app_name}</h1>
            <p className="text-sm text-slate-500 mt-1 font-mono">{app?.app_slug}</p>
            <p className="text-slate-500 mt-3 max-w-2xl">Manage this App's training from one workspace. Repeated targeting is reused automatically while Topics keep larger libraries organised.</p>
          </div>
        </div>
        <button type="button" onClick={() => navigate(buildVideoUrl())} className="bg-blue-600 hover:bg-blue-500 text-white px-5 py-3 rounded-xl flex items-center justify-center gap-2 font-semibold shadow-lg shadow-blue-900/20">
          <SafeIcon icon={FiPlus} className="w-4 h-4" /> Add Training Video
        </button>
      </div>

      {error && (
        <div className="mb-6 bg-red-500/10 border border-red-500/20 text-red-700 px-4 py-3 rounded-xl flex items-center gap-2 text-sm">
          <SafeIcon icon={FiAlertCircle} className="w-5 h-5" /> {error}
        </div>
      )}

      <section className="bg-slate-900 border border-slate-800 rounded-2xl p-5 mb-7 relative">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiSearch} className="w-5 h-5 text-blue-600" />
              <h2 className="text-base font-bold text-white">Search this App</h2>
            </div>
            <p className="text-xs text-slate-500 mt-1">Search videos, Topics, descriptions, categories, tags and keywords without leaving this workspace.</p>
          </div>
          <div className="relative w-full lg:max-w-xl">
            <SafeIcon icon={FiSearch} className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input type="search" value={workspaceQuery} onChange={(event) => setWorkspaceQuery(event.target.value)} placeholder={`Search ${app?.app_name || 'this App'} training…`} className="w-full pl-10 pr-10 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-sm text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none" />
            {workspaceQuery && <button type="button" onClick={() => setWorkspaceQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-500 hover:text-blue-600" aria-label="Clear App search"><SafeIcon icon={FiX} className="w-4 h-4" /></button>}
          </div>
        </div>

        {workspaceQuery.trim().length >= 2 && (
          <div className="mt-4 border border-slate-800 rounded-xl overflow-hidden bg-slate-950">
            <div className="px-4 py-2.5 border-b border-slate-800 text-xs text-slate-500 flex justify-between gap-4">
              <span>{searchLoading ? 'Searching this App…' : searchError || `${totalSearchResults} matching item${totalSearchResults === 1 ? '' : 's'}`}</span>
              <span>App-scoped only</span>
            </div>
            {!searchLoading && !searchError && totalSearchResults === 0 && <div className="px-5 py-7 text-center text-sm text-slate-500">No matching training content in this App.</div>}
            {searchResults.topics.map(topic => (
              <button key={`topic-${topic.id}`} type="button" onClick={() => navigate(`/playlists/${topic.id}`)} className="w-full p-4 border-b border-slate-800 last:border-b-0 flex items-start gap-3 text-left hover:bg-blue-500/5">
                <div className="w-9 h-9 rounded-xl bg-blue-500/10 text-blue-600 flex items-center justify-center"><SafeIcon icon={FiList} className="w-4 h-4" /></div>
                <div className="min-w-0 flex-1"><p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Topic</p><p className="text-sm font-semibold text-white truncate mt-0.5">{topic.name}</p><p className="text-xs text-slate-500 truncate mt-0.5">{topic.description || 'Training topic'}</p></div>
              </button>
            ))}
            {searchResults.videos.map(video => (
              <button key={`video-${video.id}`} type="button" onClick={() => navigate(`/videos/${video.id}`)} className="w-full p-4 border-b border-slate-800 last:border-b-0 flex items-start gap-3 text-left hover:bg-blue-500/5">
                <div className="w-16 h-10 rounded-lg bg-slate-900 overflow-hidden border border-slate-800 flex-shrink-0">{video.thumbnail_url ? <img src={video.thumbnail_url} alt="" className="w-full h-full object-cover" loading="lazy" /> : <div className="w-full h-full flex items-center justify-center text-slate-600"><SafeIcon icon={FiVideo} className="w-4 h-4" /></div>}</div>
                <div className="min-w-0 flex-1"><p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Video</p><p className="text-sm font-semibold text-white truncate mt-0.5">{video.title}</p><p className="text-xs text-slate-500 truncate mt-0.5">{video.description || video.category || 'Training video'}</p></div>
              </button>
            ))}
          </div>
        )}
      </section>

      <section className="bg-slate-900 border border-slate-800 rounded-2xl p-6 mb-7">
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4 mb-5">
          <div className="flex items-start gap-3">
            <div className="p-2.5 rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-600"><SafeIcon icon={FiShield} className="w-5 h-5" /></div>
            <div>
              <h2 className="text-lg font-bold text-white">Primary content scope</h2>
              <p className="text-sm text-slate-500 mt-1">This is the default for future content. Existing videos keep their own saved scope.</p>
            </div>
          </div>
          <span className={`text-xs font-bold px-3 py-1.5 rounded-full border ${setupScope === 'client_facing' ? 'bg-blue-500/10 text-blue-700 border-blue-500/20' : setupScope === 'bpas_internal_team' ? 'bg-purple-500/10 text-purple-700 border-purple-500/20' : 'bg-slate-800 text-slate-500 border-slate-700'}`}>
            {setupScope ? `Selected: ${setupScope === 'client_facing' ? 'Client Facing' : 'Internal Team'}` : 'Selection required'}
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button type="button" onClick={() => setPrimaryScope('client_facing')} aria-pressed={setupScope === 'client_facing'} className={`scope-choice text-left p-5 rounded-xl ${setupScope === 'client_facing' ? 'scope-choice-client-selected' : ''}`}>
            {setupScope === 'client_facing' && <span className="scope-choice-check"><SafeIcon icon={FiCheck} className="w-3.5 h-3.5" /></span>}
            <SafeIcon icon={FiUsers} className="scope-choice-icon w-6 h-6 text-blue-600 mb-3" />
            <p className="font-semibold text-white">Client Facing</p>
            <p className="text-sm text-slate-500 mt-1 pr-5">Training and guidance intended for users of this client application.</p>
          </button>
          <button type="button" onClick={() => setPrimaryScope('bpas_internal_team')} aria-pressed={setupScope === 'bpas_internal_team'} className={`scope-choice text-left p-5 rounded-xl ${setupScope === 'bpas_internal_team' ? 'scope-choice-internal-selected' : ''}`}>
            {setupScope === 'bpas_internal_team' && <span className="scope-choice-check"><SafeIcon icon={FiCheck} className="w-3.5 h-3.5" /></span>}
            <SafeIcon icon={FiUser} className="scope-choice-icon w-6 h-6 text-purple-500 mb-3" />
            <p className="font-semibold text-white">Internal Team</p>
            <p className="text-sm text-slate-500 mt-1 pr-5">Internal BPA operating, support, sales or administration guidance.</p>
          </button>
        </div>
      </section>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-7">
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
          <div className="flex items-center justify-between"><SafeIcon icon={FiVideo} className="w-5 h-5 text-blue-600" /><span className="text-2xl font-bold text-white">{videoCount}</span></div>
          <p className="text-sm font-semibold text-white mt-4">Videos</p><p className="text-xs text-slate-500 mt-1">App assignment is automatic when added here.</p>
        </div>
        <button type="button" onClick={() => navigate('/playlists')} className="bg-slate-900 border border-slate-800 rounded-xl p-5 text-left hover:border-blue-500/30 transition-colors">
          <div className="flex items-center justify-between"><SafeIcon icon={FiList} className="w-5 h-5 text-blue-600" /><span className="text-2xl font-bold text-white">{topicCount}</span></div>
          <p className="text-sm font-semibold text-white mt-4">Topics</p><p className="text-xs text-slate-500 mt-1">Group related videos into clear client-facing subjects or ordered sequences.</p>
        </button>
        <div className={`border rounded-xl p-5 ${trainingUrl ? 'bg-green-500/5 border-green-500/20' : 'bg-amber-500/5 border-amber-500/20'}`}>
          <div className="flex items-center justify-between"><SafeIcon icon={trainingUrl ? FiCheck : FiLock} className={`w-5 h-5 ${trainingUrl ? 'text-green-600' : 'text-amber-600'}`} /><span className={`text-xs font-bold ${trainingUrl ? 'text-green-700' : 'text-amber-700'}`}>{trainingUrl ? 'Route ready' : 'Backend later'}</span></div>
          <p className="text-sm font-semibold text-white mt-4">Client Integration</p><p className="text-xs text-slate-500 mt-1">The integration prompt is ready now; live content wiring waits for the secure route when required.</p>
        </div>
      </div>

      <section className="bg-slate-900 border border-slate-800 rounded-2xl p-6 mb-7">
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-5">
          <div><div className="flex items-center gap-2"><SafeIcon icon={FiZap} className="w-5 h-5 text-blue-600" /><h2 className="text-lg font-bold text-white">Smart defaults for the next video</h2></div><p className="text-sm text-slate-500 mt-2">Applied automatically when you add a video from this App.</p></div>
          <span className="text-xs px-3 py-1.5 rounded-full bg-slate-800 text-slate-500">Source: {smartDefaults.source}</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-5">
          <div className={`border rounded-xl p-4 ${smartDefaults.scope === 'client_facing' ? 'scope-summary-client' : smartDefaults.scope === 'bpas_internal_team' ? 'scope-summary-internal' : 'bg-slate-800/40 border-slate-800'}`}>
            <p className="text-xs text-slate-500 uppercase tracking-wider">Scope</p>
            <p className={`text-sm font-bold mt-1 ${smartDefaults.scope === 'client_facing' ? 'scope-client-text' : smartDefaults.scope === 'bpas_internal_team' ? 'scope-internal-text' : 'text-white'}`}>{scopeLabel}</p>
          </div>
          <div className="bg-slate-800/40 border border-slate-800 rounded-xl p-4"><p className="text-xs text-slate-500 uppercase tracking-wider">Audience</p><p className="text-sm font-semibold text-white mt-1 capitalize">{audienceLabel}</p></div>
          <div className="bg-slate-800/40 border border-slate-800 rounded-xl p-4"><p className="text-xs text-slate-500 uppercase tracking-wider">Channel</p><p className="text-sm font-semibold text-white mt-1">{smartDefaults.channels.length ? smartDefaults.channels.map(item => item.replaceAll('_', ' ')).join(', ') : 'Automatic on first video'}</p></div>
          <div className="bg-slate-800/40 border border-slate-800 rounded-xl p-4"><p className="text-xs text-slate-500 uppercase tracking-wider">Category</p><p className="text-sm font-semibold text-white mt-1 capitalize">{smartDefaults.category}</p></div>
        </div>
      </section>

      {topics.length > 0 && (
        <section className="mb-7">
          <div className="flex items-end justify-between gap-4 mb-4">
            <div><h2 className="text-lg font-bold text-white">Topics</h2><p className="text-sm text-slate-500 mt-1">Use Topics to group related videos without changing the underlying Playlist data model.</p></div>
            <button type="button" onClick={() => navigate(buildTopicUrl())} className="text-sm font-semibold text-blue-600 hover:text-blue-700">+ Create Topic</button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {topics.map(topic => (
              <button key={topic.id} type="button" onClick={() => navigate(`/playlists/${topic.id}`)} className="bg-slate-900 border border-slate-800 rounded-2xl p-5 text-left hover:border-blue-500/30 hover:-translate-y-0.5 transition-all shadow-sm">
                <div className="w-10 h-10 rounded-xl bg-blue-500/10 text-blue-600 flex items-center justify-center"><SafeIcon icon={FiLayers} className="w-5 h-5" /></div>
                <p className="text-sm font-bold text-white mt-4 line-clamp-1">{topic.name}</p>
                <p className="text-xs text-slate-500 mt-2 leading-5 line-clamp-2 min-h-[40px]">{topic.description || 'Organised training topic or learning sequence.'}</p>
                <div className="mt-4 flex items-center justify-between gap-3"><span className={`inline-flex px-2 py-1 rounded-full border text-[10px] uppercase tracking-wider font-bold ${workflowTone(topic.workflow_status || 'draft')}`}>{(topic.workflow_status || 'draft').replaceAll('_', ' ')}</span><span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Open →</span></div>
              </button>
            ))}
          </div>
        </section>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1.35fr_0.65fr] gap-7">
        <section className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
          <div className="p-6 border-b border-slate-800 flex items-center justify-between gap-4">
            <div><h2 className="text-lg font-bold text-white">Recent training videos</h2><p className="text-sm text-slate-500 mt-1">Professional previews make it easy to confirm content before publishing.</p></div>
            <button type="button" onClick={() => navigate(buildVideoUrl())} className="text-sm font-semibold text-blue-600 hover:text-blue-700">+ Add video</button>
          </div>
          <div className="p-5">
            {videos.length === 0 ? (
              <div className="text-center py-10"><SafeIcon icon={FiPlayCircle} className="w-10 h-10 text-slate-600 mx-auto" /><p className="text-sm font-semibold text-white mt-3">No videos yet</p><p className="text-xs text-slate-500 mt-1">Add the first video and its settings will seed future entries.</p></div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {videos.map(video => (
                  <button key={video.id} type="button" onClick={() => navigate(`/videos/${video.id}`)} className="group text-left bg-slate-950 border border-slate-800 rounded-2xl overflow-hidden hover:border-blue-500/30 transition-all">
                    <div className="aspect-video bg-slate-900 relative overflow-hidden">
                      {video.thumbnail_url ? <img src={video.thumbnail_url} alt={`${video.title || 'Training video'} thumbnail`} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300" loading="lazy" /> : <div className="w-full h-full flex items-center justify-center text-slate-600"><SafeIcon icon={FiVideo} className="w-10 h-10" /></div>}
                      <div className="absolute inset-0 bg-slate-950/10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><span className="w-11 h-11 rounded-full bg-white/90 text-slate-900 flex items-center justify-center shadow-lg"><SafeIcon icon={FiPlay} className="w-5 h-5 ml-0.5" /></span></div>
                      {formatDuration(video.duration_seconds) && <span className="absolute right-2 bottom-2 px-2 py-1 rounded-md bg-slate-950/80 text-white text-[10px] font-semibold">{formatDuration(video.duration_seconds)}</span>}
                    </div>
                    <div className="p-4">
                      <div className="flex items-center justify-between gap-2"><span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{(video.category || 'training').replaceAll('_', ' ')}</span><span className={`inline-flex px-2 py-1 rounded-full border text-[10px] uppercase tracking-wider font-bold ${workflowTone(video.workflow_status || 'draft')}`}>{(video.workflow_status || 'draft').replaceAll('_', ' ')}</span></div>
                      <h3 className="text-sm font-bold text-white mt-2 line-clamp-2">{video.title}</h3>
                      <p className="text-xs text-slate-500 mt-2 leading-5 line-clamp-2 min-h-[40px]">{video.description || 'No description added yet.'}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>

        <div className="space-y-5">
          <section className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-4"><SafeIcon icon={FiLayers} className="w-5 h-5 text-blue-600" /><h2 className="text-base font-bold text-white">Next actions</h2></div>
            <div className="space-y-3">
              <button type="button" onClick={() => navigate(buildVideoUrl())} className="w-full px-4 py-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold flex items-center justify-center gap-2"><SafeIcon icon={FiPlus} className="w-4 h-4" /> Add Training Video</button>
              <button type="button" onClick={() => navigate(buildTopicUrl())} className="w-full px-4 py-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-white text-sm font-semibold flex items-center justify-center gap-2"><SafeIcon icon={FiList} className="w-4 h-4" /> Create Topic / Playlist</button>
            </div>
          </section>

          <section className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-3"><SafeIcon icon={FiClipboard} className="w-5 h-5 text-blue-600" /><h2 className="text-base font-bold text-white">Client App Integration Prompt</h2></div>
            <p className="text-xs text-slate-500 leading-5">Copy a complete, App-specific prompt for the LLM that built the existing client application. It contains identifiers and required URLs only—never privileged credentials.</p>
            <div className="mt-4 p-3 rounded-xl bg-slate-950 border border-slate-800 text-xs text-slate-500 space-y-1.5">
              <p><span className="font-semibold text-slate-400">Video Hub:</span> {window.location.origin}</p>
              <p><span className="font-semibold text-slate-400">Scope:</span> {setupScope ? (setupScope === 'client_facing' ? 'Client Facing' : 'Internal Team') : 'Not selected'}</p>
              <p className="truncate"><span className="font-semibold text-slate-400">Training route:</span> {trainingUrl || 'Backend setup pending'}</p>
              <p><span className="font-semibold text-slate-400">Security:</span> No secrets included</p>
            </div>
            <button type="button" onClick={() => copyText('prompt', integrationPrompt)} className="w-full mt-4 px-4 py-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold flex items-center justify-center gap-2"><SafeIcon icon={copied === 'prompt' ? FiCheck : FiCopy} className="w-4 h-4" /> {copied === 'prompt' ? 'Integration Prompt Copied' : 'Copy Integration Prompt'}</button>
            {trainingUrl ? (
              <button type="button" onClick={() => copyText('route', trainingUrl)} className="w-full mt-3 px-4 py-3 rounded-xl border border-green-500/20 bg-green-500/5 text-sm font-semibold text-green-700 hover:bg-green-500/10 flex items-center justify-center gap-2"><SafeIcon icon={copied === 'route' ? FiCheck : FiExternalLink} className="w-4 h-4" />{copied === 'route' ? 'Training Route Copied' : 'Copy Training Route'}</button>
            ) : (
              <div className="mt-3 px-3 py-2.5 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-700 text-xs flex items-start gap-2"><SafeIcon icon={FiLock} className="w-4 h-4 mt-0.5 flex-shrink-0" /><span>The prompt safely marks the live route as pending rather than inventing a key or bypassing security.</span></div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default AppSetup;